<template>
    <main-layout>
        <inertia-head>
            <title>Vehicle List</title>
        </inertia-head>

        <template #header>
            <h2 class="font-bold text-xl text-white leading-tight">
                Vehicle List
            </h2>
        </template>

        <search-component :originalKeyword="originalKeyword" @search="search" />

        <!-- Filter for small screen -->
        <responsive-filter-component
            class="lg:hidden"
            :isLoaded="isLoaded"
            :total="vehicles.total"
            :originalSort="originalSort"
            :keyword="originalKeyword"
            :originalFilter="originalFilter"
            :compoundList="compounds"
            :auctions="auctions"
            :makes="makes"
            :minPrice="minPrice"
            :maxPrice="maxPrice"
            :minYear="minYear"
            :maxYear="maxYear"
            :maxMileage="maxMileage"
            @sort="sort"
            @remove="remove"
            @filter="filter"
            @clear="clear"
        />

        <!-- For Large Screen -->
        <div class="hidden lg:block py-12">
            <div class="max-w-7xl mx-auto sm:px-6 lg:px-8">
                <div class="bg-white overflow-hidden shadow-sm sm:rounded-lg">
                    <div class="p-6 bg-white border-b border-gray-200">
                        <div class="flex">
                            <!-- filter list -->
                            <div class="mr-8">
                                <filter-component
                                    :isLoaded="isLoaded"
                                    :makes="makes"
                                    :auctions="auctions"
                                    :compounds="compounds"
                                    :total="vehicles.total"
                                    :minPrice="minPrice"
                                    :maxPrice="maxPrice"
                                    :maxMileage="maxMileage"
                                    :minYear="minYear"
                                    :maxYear="maxYear"
                                    :originalFilter="originalFilter"
                                    :hidePhoto="hidePhoto"
                                    @filter="filter"
                                    @updateHidePhoto="updateHidePhoto"
                                    @clear="clear"
                                />
                            </div>

                            <div class="flex-grow">
                                <div class="flex mb-8 px-4">
                                    <!-- selected filter -->
                                    <div class="flex-grow">
                                        <selected-filter-component
                                            v-show="
                                                originalKeyword ||
                                                originalFilter
                                            "
                                            :keyword="originalKeyword"
                                            :filter="originalFilter"
                                            :compoundList="compounds"
                                            :minPrice="minPrice"
                                            :maxPrice="maxPrice"
                                            :minYear="minYear"
                                            :maxYear="maxYear"
                                            @remove="remove"
                                        />
                                    </div>

                                    <!-- sort -->
                                    <sort-component
                                        :originalSort="originalSort"
                                        @sort="sort"
                                    ></sort-component>
                                </div>

                                <!-- vehicle list -->
                                <div
                                    class="
                                        relative
                                        grid grid-cols-2
                                        xl:grid-cols-3
                                        gap-4
                                        justify-items-center
                                    "
                                >
                                    <!-- show when loading -->
                                    <div
                                        v-if="!isLoaded"
                                        class="
                                            absolute
                                            bg-white bg-opacity-50
                                            inset-0
                                            z-10
                                            cursor-not-allowed
                                        "
                                    ></div>

                                    <vehicle-component
                                        v-for="vehicle in vehicles.data"
                                        :vehicle="vehicle"
                                        :hidePhoto="hidePhoto"
                                    ></vehicle-component>
                                </div>

                                <!-- pagination -->
                                <div
                                    class="my-6 w-full flex justify-center"
                                    v-if="
                                        vehicles.links &&
                                        vehicles.links.length > 3
                                    "
                                >
                                    <pagination-component
                                        :links="vehicles.links"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- For Small Screen -->
        <div class="my-8 mx-4 lg:hidden">
            <div class="mx-auto">
                <div class="bg-white overflow-hidden shadow-sm rounded-lg">
                    <div class="bg-white border-b border-gray-200">
                        <div class="flex flex-col pt-10 pb-4">
                            <!-- vehicle list -->
                            <div
                                class="
                                    relative
                                    grid grid-cols-1
                                    gap-0
                                    justify-items-center
                                "
                            >
                                <!-- show when loading -->
                                <div
                                    v-if="!isLoaded"
                                    class="
                                        absolute
                                        bg-white bg-opacity-50
                                        inset-0
                                        z-10
                                        cursor-not-allowed
                                    "
                                ></div>

                                <vehicle-component
                                    v-for="vehicle in vehicles.data"
                                    :vehicle="vehicle"
                                ></vehicle-component>
                            </div>

                            <!-- pagination -->
                            <div
                                class="p-6 w-full flex justify-center"
                                v-if="vehicles.links"
                            >
                                <responsive-pagination-component
                                    :vehicles="vehicles"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </main-layout>
</template>

<script>
import MainLayout from "@/Layouts/Main";
import SearchComponent from "@/Components/List/Search";
import SortComponent from "@/Components/List/Sort";
import FilterComponent from "@/Components/List/Filter";
import ResponsiveFilterComponent from "@/Components/List/ResponsiveFilter";
import SelectedFilterComponent from "@/Components/List/SelectedFilter";
import DropdownComponent from "@/Components/Dropdown";
import DropdownButton from "@/Components/DropdownButton";
import VehicleComponent from "@/Components/List/Vehicle";
import PaginationComponent from "@/Components/Pagination";
import ResponsivePaginationComponent from "@/Components/ResponsivePagination";

export default {
    components: {
        MainLayout,
        SearchComponent,
        SortComponent,
        FilterComponent,
        ResponsiveFilterComponent,
        SelectedFilterComponent,
        DropdownComponent,
        DropdownButton,
        VehicleComponent,
        PaginationComponent,
        ResponsivePaginationComponent,
    },
    props: {
        vehicles: {
            type: Object,
            required: true,
        },
        originalKeyword: {
            type: String,
            default: null,
        },
        originalFilter: {
            type: Object,
            default: null,
        },
        originalSort: {
            type: String,
            default: null,
        },
        hidePhoto: {
            type: Boolean,
            default: false,
        },
        makes: {
            type: Array,
            default: null,
        },
        auctions: {
            type: Array,
            default: null,
        },
        compounds: {
            type: Array,
            default: null,
        },
    },
    data() {
        return {
            isLoaded: true,
            minPrice: 0,
            maxPrice: 100,
            maxMileage: 100,
            minYear: 2005,
            maxYear: new Date().getFullYear(),
        };
    },
    mounted() {
        this.$inertia.on("start", () => {
            this.isLoaded = false;
        });
        this.$inertia.on("finish", () => {
            this.isLoaded = true;
        });
    },
    methods: {
        fetchVehicles(keyword, selectedFilter, sortType, hidePhoto) {
            let params = {};
            if (keyword && keyword.length > 0) {
                params.keyword = keyword;
            }
            if (selectedFilter && Object.keys(selectedFilter).length > 0) {
                params.filter = selectedFilter;
            }
            if (sortType && sortType.length > 0) {
                params.sort = sortType;
            }
            params.hidePhoto = hidePhoto;
            this.$inertia.get(this.route("main"), params);
        },
        search(newKeyword) {
            this.fetchVehicles(
                newKeyword,
                this.originalFilter,
                this.originalSort,
                this.hidePhoto
            );
        },
        sort(sortType) {
            this.fetchVehicles(
                this.originalKeyword,
                this.originalFilter,
                sortType,
                this.hidePhoto
            );
        },
        filter(selectedFilter) {
            this.fetchVehicles(
                this.originalKeyword,
                selectedFilter,
                this.originalSort,
                this.hidePhoto
            );
        },
        remove(field, option) {
            if (field === "keyword") {
                this.search();
            } else {
                let filter = this.originalFilter;
                switch (field) {
                    case "make":
                        if (
                            filter.hasOwnProperty("make") &&
                            Array.isArray(filter.make) &&
                            option
                        ) {
                            const index = filter.make.indexOf(option);
                            if (index > -1) {
                                filter.make.splice(index, 1);
                            }
                        }
                        break;
                    case "auction":
                        if (
                            filter.hasOwnProperty("auction") &&
                            Array.isArray(filter.auction) &&
                            option
                        ) {
                            const index = filter.auction.indexOf(option);
                            if (index > -1) {
                                filter.auction.splice(index, 1);
                            }
                        }
                        break;
                    case "compound":
                        if (
                            filter.hasOwnProperty("auction") &&
                            Array.isArray(filter.compound) &&
                            option
                        ) {
                            const index = filter.compound.indexOf(option);
                            if (index > -1) {
                                filter.compound.splice(index, 1);
                            }
                        }
                        break;
                    default:
                        delete filter[field];
                }
                this.filter(filter);
            }
        },
        updateHidePhoto() {
            this.fetchVehicles(
                this.originalKeyword,
                this.originalFilter,
                this.originalSort,
                !this.hidePhoto
            );
        },
        clear() {
            this.fetchVehicles(null, null, null, this.hidePhoto);
        },
    },
};
</script>
